import { useCallback } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextTitle1, TextHeadline } from '@cbhq/cds-web/typography';
import { css } from '@emotion/css';

type ExploreHeaderProps = {
  activeTab: 'pools' | 'tokenized-equities';
  onTabChange: (tab: 'pools' | 'tokenized-equities') => void;
};

const TabStyles = css`
  cursor: pointer;
  padding: 16px 0;
  border: none;
  background: none;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    border-bottom-color: var(--color-fgPrimary);
  }
`;

// In the Tokenized Equities app the Pools tab is excluded from Explore.
const isTokenizedEquitiesApp = process.env.NEXT_PUBLIC_APP_MODE === 'tokenized-equities';

const TABS: { id: 'pools' | 'tokenized-equities'; label: string }[] = [
  ...(isTokenizedEquitiesApp ? [] : [{ id: 'pools' as const, label: 'Pools' }]),
  { id: 'tokenized-equities', label: 'Tokenized Equities' },
];

export function ExploreHeader({ activeTab, onTabChange }: ExploreHeaderProps) {
  // Keyboard navigation handler for tabs (accessibility)
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = currentIndex < TABS.length - 1 ? currentIndex + 1 : 0;
        onTabChange(TABS[nextIndex].id);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : TABS.length - 1;
        onTabChange(TABS[prevIndex].id);
      } else if (e.key === 'Home') {
        e.preventDefault();
        onTabChange(TABS[0].id);
      } else if (e.key === 'End') {
        e.preventDefault();
        onTabChange(TABS[TABS.length - 1].id);
      }
    },
    [onTabChange],
  );

  return (
    <VStack gap={0.5}>
      {/* Title */}
      <TextTitle1 as="h1">Explore</TextTitle1>

      {/* Tabs */}
      <HStack alignItems="center" justifyContent="space-between" width="100%">
        <HStack gap={4} alignItems="center" role="tablist" aria-label="Explore sections">
          {TABS.map((tab, index) => (
            <button
              key={tab.id}
              id={`explore-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`explore-tabpanel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              className={`${TabStyles} ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
            >
              <TextHeadline as="span" color={activeTab === tab.id ? 'fgPrimary' : 'fg'}>
                {tab.label}
              </TextHeadline>
            </button>
          ))}
        </HStack>
      </HStack>
    </VStack>
  );
}
